import { z } from 'zod';
import { FirestoreDocId } from './eos/scorecard.js';
/**
 * Approvals queue — Phase 4 of the v7 redesign (Epic #123).
 *
 * The `/approvals` surface is the single workflow for every agent draft
 * awaiting the CEO's OK. Today these are scattered across the briefing
 * `<DraftRow>`s + the alerts feed; this schema unifies the wire shape
 * so one queue surface, one bulk action, one audit trail. Drafts surface
 * to the queue from existing `agentActions` rows whose status === 'draft'
 * (a new `AgentActionStatus` value added alongside this schema — see
 * `agent-action.ts` for the enum-promotion note).
 *
 * Server-only writes from the agent runtime + the approvals endpoints;
 * clients only read the queue and POST a decision. Multi-tenant: every
 * `PendingDraft` carries `orgId`.
 */
/**
 * What state a draft is in from the CEO's perspective. Distinct from
 * `AgentActionStatus` (the lifecycle of the agent's row in `agentActions`)
 * and from `DraftStatus` (the briefing's `<DraftRow>` enum — that one
 * covers `'awaiting' | 'sent' | 'scheduled' | 'acked'` and stays as-is for
 * the Today surface).
 *
 * Lifecycle:
 *   pending  — agent has drafted; awaiting CEO decision (the queue state)
 *   approved — CEO approved; the action proceeds (eventually flips to
 *              `AgentActionStatus.completed` once the send fires)
 *   edited   — CEO edited the body before approving; same downstream as
 *              `approved` but recorded distinctly for replay + audit
 *   rejected — CEO rejected; the agent will not send
 */
export const DraftApprovalStatus = z.enum(['pending', 'approved', 'edited', 'rejected']);
/**
 * Distinguishes drafts that can be one-click bulk-approved (low risk,
 * narrow scope — e.g. routine 1:1 prep) from those that require explicit
 * review (high-impact, broad recipient list, regulatory or legal context).
 *
 * The agent runtime tags a draft `routine` only when the relevant
 * playbook + confidence + recipient class line up. Stored on the
 * `agentActions` row at draft-mint time; the approvals queue reads
 * it through.
 */
export const DraftRoutineness = z.enum(['routine', 'review-required']);
/**
 * Channel the draft will be sent through. Channel-agnostic recipient
 * format (`PendingDraft.to`) means the queue surface can render an
 * email address, a Slack user, or a doc target without branching the
 * row component.
 */
export const DraftChannel = z.enum(['email', 'slack', 'doc', 'filing', 'offer']);
/**
 * Wire shape returned by `GET /api/orgs/:o/approvals`. Composes existing
 * `AgentAction` fields (id, orgId, kind) + the approvals-queue-specific
 * fields the surface needs to render a row + per-row CTA.
 *
 * `kind` echoes `AgentActionKind` enum values (`drafted`, `proposed`,
 * etc.) so the surface can show the same all-caps action column as the
 * agent log.
 */
export const PendingDraft = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /** AgentActionKind enum value — see `agent-action.ts`. */
    kind: z.string().min(1),
    /** One-line headline shown as the row title (e.g. "Acme renewal · pre-brief to Lena"). */
    subject: z.string().max(200),
    /** Body the agent prepared; renders in the row preview + the slide-over editor. */
    body: z.string().max(8000),
    /**
     * Recipient(s) — channel-agnostic. Could be email addresses, Slack
     * handles, Notion page targets. The surface renders these through
     * `<EntityLink>` when the value is entity-resolvable; plain text
     * otherwise.
     */
    to: z.array(z.string()).default([]),
    channel: DraftChannel,
    routineness: DraftRoutineness,
    /** ISO timestamp the agent drafted this. */
    draftedAt: z.string(),
    /** Optional model confidence in this draft (0..1). */
    confidence: z.number().min(0).max(1).optional(),
});
export const PendingDraftsResponse = z.object({
    drafts: z.array(PendingDraft),
});
/**
 * Decision verb on a single draft. `approve` = send as-drafted; `edit`
 * = save the edited body (the surface treats this as still "approved"
 * downstream but records the edit distinctly for audit replay);
 * `reject` = the agent will not send.
 */
export const ApprovalDecision = z.enum(['approve', 'edit', 'reject']);
/**
 * Body of `POST /api/orgs/:o/approvals/:draftId`. `editedBody` is
 * required when `decision === 'edit'` (enforced by the `superRefine`
 * below — without it, a `{ decision: 'edit' }` body with no edit
 * would parse fine, the route would write `decision: 'edit'` with no
 * body delta, and the audit trail would claim an edit happened);
 * `rejectionReason` is optional even on `reject` (the CEO may decline
 * without a written reason).
 */
export const ApprovalRequest = z
    .object({
    decision: ApprovalDecision,
    editedBody: z.string().max(8000).optional(),
    rejectionReason: z.string().max(500).optional(),
})
    .superRefine((val, ctx) => {
    if (val.decision === 'edit' && val.editedBody === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'editedBody is required when decision is "edit"',
            path: ['editedBody'],
        });
    }
});
/**
 * Body of `POST /api/orgs/:o/approvals/bulk-approve`. The 50-item cap
 * keeps the per-call audit fan-out bounded — at 50 drafts × one audit
 * write each, the route still completes within a single Cloud Functions
 * invocation budget.
 *
 * Each `draftId` is validated through `FirestoreDocId` at the schema
 * boundary so a malformed id can't slip through and crash the
 * per-draft loop mid-write. (Pre-PR-`#136`, the router caught bad ids
 * inside the loop, which meant earlier drafts could be approved
 * irrevocably before the request 400'd.)
 */
export const BulkApprovalRequest = z.object({
    draftIds: z.array(FirestoreDocId).min(1).max(50),
});
